import { useState } from 'react'
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd'
import { DollarOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import type { FulfillmentOrderRoot } from '@repo/sdk'

import { ProductCell } from '../../components/ProductCell'
import { StatusBadge } from '../../components/StatusBadge'
import { formatKrw } from '@repo/shared'
import { canRefundOrderShippingFee, getOrderPaidAmount } from '../../lib/orderShippingAmounts'
import { getCancelledStatusLabel } from './getCancelledStatusLabel'
import { useOrderStatusConfig } from './orderStatusConfig'
import { OrderTypeBadge } from './OrderTypeBadge'
import { ShippingFeeRefundModal } from './ShippingFeeRefundModal'
import { formatOrderLabel } from './orderDisplay'

const ORDER_TERMINAL_STATUSES = ['CANCELLED', 'RETURNED', 'EXPIRED']

interface OrderItemsCardProps {
  order: FulfillmentOrderRoot
}

export function OrderItemsCard({ order }: OrderItemsCardProps) {
  const { t } = useTranslation()
  const baseStatusConfig = useOrderStatusConfig()
  const [shippingFeeRefundOrderId, setShippingFeeRefundOrderId] = useState<string | null>(null)

  // Shipping-fee-only refund, one charge at a time. Under the separated contract the button sits
  // beside the charge on the member order that paid it (#78) and only while that charge has a
  // remaining amount the server calls refundable; legacy responses keep the header button and the
  // FO-wide rule.
  const isSeparated = order.shippingCharges !== undefined
  return (
    <Card title={t('pages.orders.sections.individualOrders')}>
      {order.orders.map((childOrder, index) => {
        const cancelledLabel = getCancelledStatusLabel(
          childOrder.activeCancellation?.initiatedBy,
          baseStatusConfig.CANCELLED.label,
          t,
        )
        const statusConfig = {
          ...baseStatusConfig,
          CANCELLED: { ...baseStatusConfig.CANCELLED, label: cancelledLabel },
        }

        return (
          <div
            key={childOrder.orderId}
            data-testid={`order-item-row-${childOrder.orderId}`}
            style={
              index > 0
                ? { marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }
                : undefined
            }
          >
            <Space style={{ marginBottom: 8 }} wrap>
              <Typography.Text strong>{formatOrderLabel(childOrder)}</Typography.Text>
              <OrderTypeBadge
                orderType={childOrder.orderType}
                auctionType={childOrder.auctionType}
                saleType={childOrder.saleType}
              />
              <StatusBadge status={childOrder.status} configMap={statusConfig} />
              {childOrder.activeCancellation &&
                ['REQUESTED', 'APPROVED'].includes(childOrder.activeCancellation.status) && (
                  <Tag color="red" data-testid={`order-cancel-badge-${childOrder.orderId}`}>
                    {t('pages.orders.fields.activeCancellation')}
                  </Tag>
                )}
              {childOrder.activeReturn && (
                <Tag color="volcano" data-testid={`order-return-badge-${childOrder.orderId}`}>
                  {t('pages.orders.fields.activeReturn')}
                </Tag>
              )}
              {!isSeparated &&
                canRefundOrderShippingFee(order, childOrder) &&
                !ORDER_TERMINAL_STATUSES.includes(childOrder.status) && (
                  <Button
                    size="small"
                    icon={<DollarOutlined />}
                    onClick={() => setShippingFeeRefundOrderId(childOrder.orderId)}
                    data-testid={`order-shipping-fee-refund-btn-${childOrder.orderId}`}
                  >
                    {t('common.buttons.shippingFeeRefund')}
                  </Button>
                )}
            </Space>

            <Descriptions column={1} size="small">
              {childOrder.orderItems.map((item) => (
                <Descriptions.Item
                  key={item.productId}
                  label={
                    <ProductCell name={item.productName} thumbnailUrl={item.productImageUrl} />
                  }
                >
                  {item.quantity} x {formatKrw(item.unitPrice.amount)} ={' '}
                  {formatKrw(item.subtotal.amount)}
                </Descriptions.Item>
              ))}
              {isSeparated && childOrder.shippingCharge && (
                <Descriptions.Item label={t('pages.orders.fields.shippingFee')}>
                  <Space data-testid={`order-shipping-fee-${childOrder.orderId}`}>
                    <span>{formatKrw(childOrder.shippingCharge.amount.amount)}</span>
                    {childOrder.shippingCharge.remainingAmount.amount === 0 ? (
                      <Tag
                        color="green"
                        data-testid={`order-shipping-fee-refunded-${childOrder.orderId}`}
                      >
                        {t('pages.orders.fields.shippingFeeRefunded')}
                      </Tag>
                    ) : (
                      canRefundOrderShippingFee(order, childOrder) &&
                      !ORDER_TERMINAL_STATUSES.includes(childOrder.status) && (
                        <Button
                          size="small"
                          icon={<DollarOutlined />}
                          onClick={() => setShippingFeeRefundOrderId(childOrder.orderId)}
                          data-testid={`order-shipping-fee-refund-btn-${childOrder.orderId}`}
                        >
                          {t('common.buttons.shippingFeeRefund')}
                        </Button>
                      )
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('pages.orders.fields.totalPaymentAmount')}>
                <strong>{formatKrw(getOrderPaidAmount(childOrder))}</strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )
      })}

      {shippingFeeRefundOrderId && (
        <ShippingFeeRefundModal
          orderId={shippingFeeRefundOrderId}
          open={shippingFeeRefundOrderId !== null}
          onClose={() => setShippingFeeRefundOrderId(null)}
        />
      )}
    </Card>
  )
}
